// Authoritative service catalog for Nonprofit Navigation Consulting, LLC
// Each entry feeds the Services overview, the dynamic /services/:slug detail page,
// and (later) the admin CMS. Content can be expanded per area over time.

export const SERVICES = [
  {
    slug: "nonprofit-formation",
    number: "01",
    title: "Nonprofit Formation",
    category: "Governance",
    summary:
      "Articles of incorporation, bylaws, 501(c)(3) federal tax-exemption applications, state charitable registrations, and foundational governance documents — together with entity structure for hybrid social enterprises and for-profit firms positioning for government contracting.",
    overview:
      "We help founders move from mission idea to fully registered, IRS-recognized public charity. Our formation engagement is engineered to produce a compliant entity that is also operationally ready on day one.",
    deliverables: [
      "Entity selection guidance (501(c)(3), 501(c)(4), 501(c)(6), fiscal sponsorship)",
      "Articles of Incorporation drafting and state filing",
      "IRS Form 1023 / 1023-EZ application preparation",
      "Bylaws and conflict-of-interest policy",
      "EIN, state charitable registration, and initial annual filings",
    ],
    audience: ["Founders", "Faith-based initiatives", "Community coalitions"],
  },
  {
    slug: "board-development-governance",
    number: "02",
    title: "Board Development & Governance",
    category: "Governance",
    summary:
      "Recruiting, orienting, and developing high-functioning boards — for nonprofits and for the closely held boards of government contractors — that meet fiduciary obligations and govern with strategic clarity.",
    overview:
      "A capable, engaged board is the single greatest predictor of nonprofit durability. We work with chairs, executive directors, and full boards to build governance systems that actually function.",
    deliverables: [
      "Board composition matrix and recruitment strategy",
      "Onboarding curriculum and orientation materials",
      "Committee structure design (finance, governance, development, program)",
      "Annual board self-assessment and executive evaluation",
      "Policy review: conflicts, whistleblower, document retention, gift acceptance",
    ],
    audience: ["Boards of Directors", "Executive Directors", "Governance committees"],
  },
  {
    slug: "strategic-planning",
    number: "03",
    title: "Strategic Planning",
    category: "Direction",
    summary:
      "Multi-year strategic plans grounded in mission, evidence, and the realities of the operating environment — for nonprofits, public agencies, and government contractors alike.",
    overview:
      "We facilitate strategic planning that produces plans organizations actually use. Our process pairs stakeholder engagement with environmental scans, financial modeling, and measurable goals tied to budget and staff capacity.",
    deliverables: [
      "Stakeholder and community engagement",
      "SWOT, PESTLE, and competitive landscape analysis",
      "Theory of change and logic model",
      "3 to 5 year strategic plan with KPIs",
      "Annual operating plan and dashboard",
    ],
    audience: ["Executive leadership", "Boards", "Senior management"],
  },
  {
    slug: "grant-research-writing",
    number: "04",
    title: "Grant Research & Grant Writing",
    category: "Funding",
    summary:
      "Prospect identification, eligibility screening, and persuasive proposal development for federal, state, foundation, and corporate funders — including hybrid pursuits where grants, cooperative agreements, and government contracts converge.",
    overview:
      "We translate program vision into competitive applications. Our writing team is grounded in federal compliance frameworks and produces narratives that survive merit review.",
    deliverables: [
      "Custom funder prospect list with deadlines and award sizes",
      "Letters of Inquiry and concept papers",
      "Federal proposals (SF-424, Project Narrative, Budget Narrative)",
      "Foundation and corporate proposals",
      "Logic models, evaluation plans, and sustainability sections",
    ],
    audience: ["Nonprofits", "Municipalities", "Educational institutions", "Tribal entities"],
  },
  {
    slug: "grant-administration-compliance",
    number: "05",
    title: "Grant Administration & Compliance",
    category: "Funding",
    summary:
      "Post-award administration aligned to 2 CFR 200 (Uniform Guidance) and funder-specific terms — coordinated, when needed, with FAR/DFARS contract administration for entities that hold both assistance and acquisition awards.",
    overview:
      "Award is the beginning, not the end. We stand up the systems that keep your federal and foundation awards in good standing and audit-ready.",
    deliverables: [
      "Award setup, internal controls, and segregation of duties",
      "Allowable cost determinations and procurement standards",
      "Time and effort, indirect cost rate, and cost allocation plans",
      "Sub-recipient monitoring and risk assessment",
      "Single Audit readiness and corrective action planning",
    ],
    audience: ["Grants managers", "Finance directors", "Program officers"],
  },
  {
    slug: "program-evaluation-assessment",
    number: "06",
    title: "Program Evaluation & Assessment",
    category: "Impact",
    summary:
      "Formative and summative evaluation, performance measurement, and outcomes reporting — for grant-funded programs, cooperative agreements, and contract-funded performance work that funders, agencies, and contracting officers trust.",
    overview:
      "We design evaluation that informs program improvement and substantiates impact. Methodologies span participatory, quasi-experimental, and mixed-methods designs.",
    deliverables: [
      "Evaluation plan with logic model and indicators",
      "Instrument design (surveys, interview protocols, focus groups)",
      "Data collection, cleaning, and analysis",
      "Outcomes and impact reports for funders and boards",
      "Continuous quality improvement systems",
    ],
    audience: ["Program directors", "Funders", "Coalition leads"],
  },
  {
    slug: "fundraising-strategy",
    number: "07",
    title: "Fundraising Strategy",
    category: "Funding",
    summary:
      "Diversified, multi-year development plans that balance grants, individual giving, events, earned revenue (including from government contracting), and major gifts.",
    overview:
      "Sustainable fundraising is not one channel — it is a system. We build a plan tailored to your donor base, geography, and staff capacity.",
    deliverables: [
      "Revenue diversification analysis",
      "Case for support and brand messaging",
      "Annual fund and major gifts plan",
      "Donor cultivation cycles and stewardship calendar",
      "CRM selection and data hygiene guidance",
    ],
    audience: ["Development directors", "Executive Directors", "Capital campaign committees"],
  },
  {
    slug: "capacity-building",
    number: "08",
    title: "Capacity Building",
    category: "Governance",
    summary:
      "Targeted investments in people, systems, and processes that strengthen the institution itself — for nonprofits, public agencies, and government contractors scaling toward larger awards.",
    overview:
      "Capacity-building engagements address the underlying conditions that make programs sustainable: leadership, finance, technology, talent, and culture.",
    deliverables: [
      "Organizational assessment across 7 capacity domains",
      "Finance and back-office system review",
      "Technology and data infrastructure roadmap",
      "Talent and succession planning",
      "Coaching for emerging leaders",
    ],
    audience: ["Small and mid-size nonprofits", "Community-based organizations"],
  },
  {
    slug: "crisis-management",
    number: "09",
    title: "Crisis Management",
    category: "Direction",
    summary:
      "Rapid stabilization support for organizations and government contractors facing leadership transition, financial distress, reputational risk, or operational disruption.",
    overview:
      "When the situation is acute, we serve as a steady hand. Our crisis engagements are confidential, fast, and focused on protecting mission and people.",
    deliverables: [
      "Rapid diagnostic and risk register",
      "Interim leadership and continuity planning",
      "Cash flow triage and creditor communication",
      "Crisis communications and stakeholder messaging",
      "Board emergency response protocols",
    ],
    audience: ["Boards", "Funders requiring intervention", "Executive leadership"],
  },
  {
    slug: "business-structure-plans",
    number: "10",
    title: "Business Structure & Business Plans",
    category: "Enterprise",
    summary:
      "Business planning, structure, management consulting, and advocacy for social enterprises, small businesses, and government contractors.",
    overview:
      "From a tight executive summary to a full extended business plan, we deliver the documents and analysis that lenders, investors, boards, and federal contracting officers expect — for nonprofits with earned-revenue lines, for small businesses, and for firms positioning to sell goods and services to government.",
    subAreas: [
      { label: "Executive Summaries (short form)", detail: "Concise narrative for investors and lenders." },
      { label: "Standard Business Plan (12–15 pages, client supplies data)", detail: "Operating plan, market analysis, financials." },
      { label: "Extended Business Plan (20+ pages)", detail: "Full diligence-grade plan with deep market and scenario analysis." },
      { label: "Government Contracting Business Setup", detail: "Entity structure, ownership, NAICS codes, and growth plan engineered for federal, state, and local contracting." },
      { label: "Management Consulting", detail: "Operations, structure, leadership and team design." },
      { label: "Outsourced External Relations / Networking / Business Intelligence", detail: "Targeted relationship and intelligence support." },
      { label: "Political Advocacy (City, County, State, Federal)", detail: "Government relations and policy engagement." },
    ],
    deliverables: [
      "Market and competitive analysis",
      "Financial projections and break-even analysis",
      "Governance and ownership structure",
      "Implementation roadmap",
    ],
    audience: ["Founders", "Social enterprises", "Small business owners", "Government contractors"],
  },
  {
    slug: "all-things-grants-management",
    number: "11",
    title: "All Things Grants Management",
    category: "Funding",
    summary:
      "End-to-end grants and cooperative agreement lifecycle — from capture to closeout — for nonprofits, public agencies, and educational institutions that pursue and steward federal awards.",
    overview:
      "This is our deepest practice on the funding side of federal work. Grants and cooperative agreements are forms of federal financial assistance governed by 2 CFR 200 (Uniform Guidance) — distinct from FAR-based government contracts, which are covered under our Government Contracting practice. We bring discipline, documentation, and federal-grade rigor to assistance awards.",
    subAreas: [
      { label: "Prospect Research (Capture)", detail: "Targeted opportunity identification and capture planning." },
      { label: "Pre-award Research & Administration", detail: "Eligibility, partner formation, and proposal architecture." },
      { label: "Data Gathering & Analyses", detail: "Quantitative and qualitative evidence to substantiate need and approach." },
      { label: "Narrative Creation, Editing, Revisions", detail: "Professional writing, rewriting, and editorial review." },
      { label: "Convening of Partners and Stakeholders", detail: "Facilitating coalitions, MOUs, and consortia agreements." },
      { label: "Budget Creation (financials and narrative)", detail: "Federally allowable, defensible budgets and narratives." },
      { label: "Final Submission Walk-Through", detail: "Pre-submission QA across Grants.gov, eRA Commons, JustGrants, and others." },
      { label: "Periodic Reporting & Compliance", detail: "Quarterly, semi-annual, annual, biennial, and final reports." },
      { label: "Grant Recordkeeping", detail: "Five and seven-year Federal recordkeeping in compliance with Uniform Guidance." },
    ],
    deliverables: [
      "Grants management policies and procedures manual",
      "Federal compliance file structure",
      "Reporting calendar and accountability matrix",
    ],
    audience: ["Federal grant recipients", "Sub-recipients", "Pass-through entities"],
  },
  {
    slug: "grant-project-management",
    number: "12",
    title: "Grant Project Management",
    category: "Funding",
    summary:
      "Ongoing project management of active grants and cooperative agreements — and parallel coordination with government contract project work — keeping scope, schedule, budget, and compliance aligned through closeout.",
    overview:
      "We embed alongside your team to drive grant-funded projects to successful, compliant completion. Status tracking, partner coordination, drawdowns, and reporting all run through a disciplined cadence.",
    deliverables: [
      "Project charter and work plan",
      "Weekly status, risks, and decisions log",
      "Drawdown and financial tracking",
      "Partner and sub-recipient coordination",
      "Closeout and final reporting",
    ],
    audience: ["Federal award recipients", "Multi-partner consortia"],
  },
  {
    slug: "subject-matter-expert-consulting",
    number: "13",
    title: "Subject Matter Expert Consulting",
    category: "Enterprise",
    summary:
      "On-demand expertise across our practice areas — including nonprofit operations, federal grants, government contracting, and a broad set of sector domains (housing, community and economic development, workforce, brownfields, public infrastructure, education, and more) — for organizations that need targeted, time-bound advisory support.",
    overview:
      "Sometimes the need is not a full engagement but a sharp, expert read on a specific question. Our Subject Matter Expert (SME) practice consolidates the sector expertise of the firm — from housing and community development to public infrastructure, education, energy, and beyond — into focused, documented, and actionable engagements delivered by senior practitioners with credentials in nonprofit leadership, federal grants administration, FAR/DFARS contracting, and public sector finance.",
    deliverables: [
      "Issue framing and decision memos",
      "Expert review of plans, proposals, contracts, and budgets",
      "Briefings for boards, councils, contracting officers, and committees",
      "Targeted research and synthesis",
    ],
    audience: ["Executives", "Elected officials", "Foundation program officers", "Government contracting officers", "Prime contractors"],
    smeAreas: [
      {
        key: "community-economic-development",
        label: "Community / Economic Development",
        body: "Place-based strategies that connect community priorities to capital, programs, and policy. We work with municipalities, county commissions, regional councils, and CDFIs to design and implement community and economic development strategies — including community needs assessment and asset mapping, economic development strategy and target-sector selection, CDBG / EDA / USDA / ARC funding alignment, public-private partnership structuring, and performance dashboards for elected officials.",
      },
      {
        key: "housing",
        label: "Housing",
        body: "Specialized housing advisory across three tracks: (a) Energy Efficiency & Renewable Energy — weatherization, decarbonization, IRA/IIJA program alignment, EnergyStar and Passive House pathways, and utility partnerships; (b) Transitional Housing & Sober Living — recovery-oriented housing design, HUD Continuum of Care alignment, peer recovery integration, and outcomes measurement; and (c) ADA Housing & ICD Housing — accessibility consulting for ADA, Fair Housing, and Intermediate Care for Individuals with Intellectual and Developmental Disabilities settings. Deliverables include funding stack design (HOME, CDBG, LIHTC, IRA tax credits), project feasibility and pro-forma review, operating models for specialized populations, and compliance frameworks — for housing developers, behavioral health providers, public housing authorities, and the contractors that build, retrofit, and operate the projects.",
      },
      {
        key: "workforce-development",
        label: "Workforce Development",
        body: "Sector strategies, registered and pre-apprenticeship pathways, WIOA / DOL / ETA-aligned program design and evaluation, employer engagement and earn-and-learn models, and outcomes tracking — with particular attention to rural and Appalachian labor markets and to contractor employers delivering federally funded work. Engagements support workforce boards, community colleges, and employer consortia.",
      },
      {
        key: "brownfields",
        label: "Brownfields & Land Recovery",
        body: "Helping communities transform underutilized and contaminated sites into productive assets — coordinating environmental assessment, remediation planning, redevelopment vision, and capital stack design. EPA Brownfields Assessment, RLF, and Cleanup grant applications; Phase I/II Environmental Site Assessment scoping and procurement; site reuse planning and community engagement; public-private redevelopment structuring; and cleanup/redevelopment compliance — coordinated with environmental and construction contractors performing the work.",
      },
      {
        key: "public-infrastructure",
        label: "Public Infrastructure",
        body: "Water, sewer, broadband, transportation, and resilience infrastructure — from planning through closeout. Capital improvement planning; environmental review (NEPA, SHPO) coordination; federal grant applications and budget narratives across IIJA, USDA Rural Development, ARC, EDA, and state-level funding; Davis-Bacon and Build America, Buy America (BABA) compliance; and closeout, asset capitalization, and reporting — across municipalities, public service districts, counties and authorities, and the engineering and construction contractors they retain.",
      },
      {
        key: "transportation",
        label: "Transportation",
        body: "Public and rural transit planning, transportation infrastructure grants (FHWA, FTA, BUILD/RAISE, INFRA, SS4A), multimodal connectivity, and freight/logistics workforce alignment.",
      },
      {
        key: "education",
        label: "Education",
        body: "K–12, Adult Education, Higher Education Management and Leadership Training, Bilingual / ESL, Special Needs Students and ADA — together with charter school start-up, 21st CCLC programming, TRIO renewals, GEAR UP, and Title I / Title IV programs. Engagements include program design and curriculum alignment, educational program evaluation, strategic enrollment and student-success planning, and board / trustee training — for school districts, higher education institutions, educational nonprofits, and contractor-delivered education services.",
      },
      {
        key: "tribal-sovereign-nations",
        label: "Tribal / Sovereign Nations",
        body: "Consulting with and in support of Tribal / Sovereign Nations — respecting government-to-government relationships, self-determination, and the unique federal funding streams (BIA, IHS, HUD ONAP, DOI, USDA RD, Treasury) available to Tribes and Tribal Designated Housing Entities.",
      },
      {
        key: "agriculture",
        label: "Agriculture",
        body: "USDA Rural Development, NRCS, FSA, AMS, and Specialty Crop programs; farm-to-school and food systems; cooperative and producer-association development; rural broadband and infrastructure connecting agricultural communities.",
      },
      {
        key: "fine-arts",
        label: "Fine Arts Consulting",
        body: "Nonprofit arts organizations, public art programs, NEA and state arts council grant strategy, arts education partnerships, cultural district and creative economy planning.",
      },
      {
        key: "religious-faith",
        label: "Religious / Faith Communities",
        body: "Advisory for parishes, dioceses, congregations, ministries, and faith-based nonprofits — with special emphasis on the Catholic Life, Catholic Education, Catholic Social Teachings, and Catholic Social Justice tradition; and on Interfaith Issues and ecumenical collaboration in service of community well-being.",
      },
      {
        key: "health-care",
        label: "Health Care",
        body: "FQHCs, behavioral health and substance use disorder providers, rural hospitals and Critical Access Hospitals, HRSA and SAMHSA program design, public health coalitions, value-based care readiness, and community health needs assessments.",
      },
      {
        key: "sbir-sttr",
        label: "SBIR / STTR",
        body: "Small Business Innovation Research and Small Business Technology Transfer programs — Phase I, II, and IIB strategy; topic and agency targeting (DoD, NIH, NSF, DOE, NASA, USDA, etc.); commercialization plans; and Phase III transition.",
      },
      {
        key: "energy",
        label: "Energy",
        body: "Energy Efficiency and Renewable Energy program design and funding (DOE, IRA, IIJA, weatherization, utility partnerships); Training and Retraining of the Energy Workforce — including transitions from legacy fossil sectors to advanced energy, grid, and storage trades.",
      },
      {
        key: "construction-building-trades",
        label: "Construction and Building Trades",
        body: "Davis-Bacon and Build America, Buy America compliance; registered apprenticeships in the building trades; community benefits agreements; project labor agreements; pre-apprenticeship pipelines and workforce diversity strategies.",
      },
    ],
  },
  // ---------------- GOVERNMENT CONTRACTING LANE ----------------
  {
    slug: "gov-contracting-capture-proposals",
    number: "14",
    title: "Government Contracting — Capture, Proposals & Bids",
    category: "Government Contracting",
    summary:
      "Capture planning and proposal development for federal, state, and local contracts, cooperative agreements, and competitive quotes — RFP, RFQ, RFI, Sources Sought, IDIQ task orders, and GSA Schedule (MAS).",
    overview:
      "We help for-profit firms — and the occasional hybrid social enterprise — win government work. This is the contracting side of the house: FAR-based acquisitions, GSA Multiple Award Schedule, and the cooperative agreements that span the line between assistance and acquisition. Our work is focused on win probability, compliance, and discipline through the entire capture-to-award cycle.",
    subAreas: [
      { label: "Sources Sought & RFI Responses", detail: "Position your firm early; shape the requirement when permissible." },
      { label: "RFP Proposal Development", detail: "Technical, management, past performance, and price volumes — fully compliant and color-team reviewed." },
      { label: "RFQ / Quote Preparation", detail: "Competitive quoting under GSA MAS, simplified acquisition, and commercial item procedures." },
      { label: "IDIQ & BPA Task Order Bids", detail: "Task-order-level capture under existing indefinite-delivery vehicles." },
      { label: "GSA Schedule (MAS) Offers & Modifications", detail: "New offers, refresh modifications, pricing additions, and SIN realignments." },
      { label: "Cooperative Agreements & OTAs", detail: "Mixed-instrument acquisitions, Broad Agency Announcements, and Other Transaction Authority responses." },
      { label: "State & Local Solicitations", detail: "State purchasing portals, local government bids, and cooperative purchasing vehicles." },
    ],
    deliverables: [
      "Capture plan and win strategy",
      "Compliance matrix and proposal outline",
      "Technical and management narratives",
      "Past performance volumes (CPARS-aligned)",
      "Price-to-win analysis and pricing build-up",
      "Color-team reviews (Pink, Red, Gold)",
    ],
    audience: ["Government contractors (prime and sub)", "Small businesses pursuing federal work", "8(a), HUBZone, WOSB, SDVOSB firms"],
  },
  {
    slug: "gov-contracting-registrations-certifications",
    number: "21",
    title: "Federal Registrations & Small Business Certifications",
    category: "Government Contracting",
    summary:
      "SAM.gov, UEI, CAGE, NAICS positioning, and small business socioeconomic certifications including 8(a), HUBZone, WOSB/EDWOSB, SDVOSB, VOSB, and SDB.",
    overview:
      "Eligibility is the price of admission to government contracting. We handle the registrations, certifications, and renewals that determine whether your firm can compete at all — and, when applicable, whether you can compete in set-aside lanes that materially increase win probability.",
    subAreas: [
      { label: "SAM.gov Registration & Renewals", detail: "Entity registration, UEI, CAGE assignment, and annual renewal." },
      { label: "NAICS & PSC Strategy", detail: "Primary and secondary code selection aligned with size standards and pipeline." },
      { label: "SBA 8(a) Business Development Program", detail: "Application, narrative, financial documentation, and program management." },
      { label: "HUBZone Certification", detail: "Eligibility verification, mapping, and certification packaging." },
      { label: "WOSB / EDWOSB Certification", detail: "Women-Owned and Economically Disadvantaged WOSB self-certification and third-party certification." },
      { label: "SDVOSB / VOSB Certification", detail: "VA Center for Verification and Evaluation packaging and renewals." },
      { label: "Small Disadvantaged Business (SDB)", detail: "SDB self-representation and supporting documentation." },
      { label: "State & Local Vendor Registrations", detail: "State procurement portals, MBE/WBE/DBE certifications where applicable." },
    ],
    deliverables: [
      "Complete registration and certification packages",
      "Eligibility memos and corrective action plans",
      "Renewal calendar and recertification triage",
    ],
    audience: ["Small businesses entering federal market", "Existing contractors expanding socioeconomic posture"],
  },
  {
    slug: "gov-contracting-administration-compliance",
    number: "16",
    title: "Government Contract Administration & FAR/DFARS Compliance",
    category: "Government Contracting",
    summary:
      "Post-award contract administration aligned to the FAR, DFARS, and agency supplements — including CPARS, invoicing, modifications, teaming and subcontracting plans, and closeout.",
    overview:
      "Winning the contract is the start. We help firms execute against terms and conditions, document past performance, manage modifications, and avoid the compliance pitfalls that lead to lost re-competes — or worse. This is the contract-side analog to our Grant Administration practice, but built around FAR Part 42, DFARS, and agency-specific clauses.",
    subAreas: [
      { label: "FAR / DFARS Compliance Program", detail: "Clause flow-down, policies, procedures, and internal controls." },
      { label: "CPARS / Past Performance", detail: "Active management of evaluations and remediation of negative ratings." },
      { label: "Invoicing & Wide Area Workflow (WAWF / iRAPT)", detail: "Compliant invoicing, payment troubleshooting, and accounts receivable hygiene." },
      { label: "Contract Modifications & Change Orders", detail: "Request for Equitable Adjustment (REA), constructive change documentation, and mod processing." },
      { label: "Teaming Agreements, JVs & Mentor-Protégé", detail: "Teaming agreements, joint venture structuring, SBA Mentor-Protégé arrangements." },
      { label: "Subcontracting Plans", detail: "FAR 52.219-9 subcontracting plans, eSRS reporting, and small business utilization." },
      { label: "Cybersecurity & CMMC", detail: "DFARS 252.204-7012, NIST SP 800-171 readiness, and CMMC posture advisory." },
      { label: "Contract Closeout", detail: "Final invoice, release of claims, property disposition, and record retention." },
    ],
    deliverables: [
      "Contract administration playbook",
      "CPARS narrative drafts and response strategies",
      "Mod packages and REA documentation",
      "Subcontracting plans and eSRS submissions",
      "Closeout documentation",
    ],
    audience: ["Prime contractors", "Subcontractors", "Small business contractors managing growth"],
  },
];

export const SERVICE_CATEGORIES = [
  { key: "Governance", label: "Governance" },
  { key: "Direction", label: "Direction" },
  { key: "Funding", label: "Funding & Grants" },
  { key: "Impact", label: "Impact" },
  { key: "Government Contracting", label: "Government Contracting" },
  { key: "Enterprise", label: "Enterprise" },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
